import { FIRST_INSTALL_ONBOARDING_SETUP_SOURCE } from "@redotech/account-common/onboarding";
import type { Team } from "@redotech/redo-model/team";
import { getRegisteredProducts } from "../registry/product-registry";
import type { Product, SetupProgress } from "../registry/types";
import { isProductEnabledForTeam } from "./is-product-enabled";
import { pickProductByRoute } from "./pick-product-by-route";
import { shouldCreateRowFromEntrySource } from "./should-create-row-from-entry-source";

type StartMerchantSetupProgress = (input: {
  product: Product;
  entrySource?: string | null;
  resetDismissed?: boolean;
  resetIntake?: boolean;
  resetDeferred?: boolean;
}) => Promise<SetupProgress | undefined>;

export async function consumeSetupGuideEntrySource({
  source,
  pathname,
  team,
  activeProduct,
  progressByProduct,
  startMerchantSetupProgress,
  applyRowToCache,
  refetchProgress,
  registeredProducts = getRegisteredProducts(),
}: {
  source: string;
  pathname: string;
  team: Team | null | undefined;
  activeProduct: Product | null;
  progressByProduct: Partial<Record<Product, SetupProgress>>;
  startMerchantSetupProgress: StartMerchantSetupProgress;
  applyRowToCache: (row: SetupProgress) => void;
  refetchProgress?: () => Promise<unknown>;
  registeredProducts?: readonly Product[];
}): Promise<boolean> {
  if (!team) {
    return false;
  }
  const targetProduct =
    pickProductByRoute(pathname, registeredProducts) ??
    activeProduct ??
    registeredProducts[0] ??
    null;
  if (!targetProduct) {
    return false;
  }
  const existingRow = progressByProduct[targetProduct] ?? null;
  const shouldResetShopperAgentProgress =
    targetProduct === "shopperAgent" &&
    existingRow != null &&
    !isProductEnabledForTeam("shopperAgent", team) &&
    (existingRow.dismissedAt != null ||
      existingRow.intakeCompletedAt != null ||
      existingRow.deferredUntil != null);
  if (shouldResetShopperAgentProgress) {
    try {
      const row = await startMerchantSetupProgress({
        product: targetProduct,
        entrySource: source,
        resetDismissed: existingRow.dismissedAt != null,
        resetIntake: existingRow.intakeCompletedAt != null,
        resetDeferred: existingRow.deferredUntil != null,
      });
      if (row) {
        applyRowToCache(row);
      }
      return true;
    } catch {
      return false;
    }
  }
  const shouldCreate = shouldCreateRowFromEntrySource({
    team,
    product: targetProduct,
    hasExistingRow: !!existingRow,
    source,
  });
  if (!shouldCreate) {
    return true;
  }
  try {
    const row = await startMerchantSetupProgress({
      product: targetProduct,
      entrySource: source,
    });
    if (row) {
      applyRowToCache(row);
      if (source === FIRST_INSTALL_ONBOARDING_SETUP_SOURCE) {
        await refetchProgress?.().catch(() => undefined);
      }
    }
    return true;
  } catch (err) {
    // Keep the URL param in place so refresh or route retry can recover.
    console.warn("setup-guide: startMerchantSetupProgress failed", {
      product: targetProduct,
      source,
      err,
    });
    return false;
  }
}
